import * as React from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import { Helmet } from "react-helmet"
import "../styles/navbar.css"

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [activeDropdown, setActiveDropdown] = React.useState(null)
  const [isMobile, setIsMobile] = React.useState(false)
  const [activeServiceGroup, setActiveServiceGroup] = React.useState(null)
  
  // Get services data
  const data = useStaticQuery(graphql`
    query {
      allServicesJson {
        group(field: { type: SELECT }) {
          fieldValue
          nodes {
            id
            slug
            title
            type
          }
        }
      }
    }
  `)
  
  const serviceGroups = data.allServicesJson.group || []
  
  // Add Second Oeuvre as a special service group
  const secondOeuvreGroup = {
    fieldValue: 'Second Oeuvre',
    nodes: [
      {
        id: 'second-oeuvre',
        slug: 'second-oeuvre',
        title: 'Voir tous les services de second oeuvre',
        type: 'second-oeuvre'
      }
    ]
  }
  
  // Add Second Oeuvre to the beginning of service groups
  const allServiceGroups = [secondOeuvreGroup, ...serviceGroups]

  // Check if mobile on mount and resize
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1100)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const toggleDropdown = (dropdown, isOpen) => {
    setActiveDropdown(isOpen ? dropdown : null);
  }

  const handleDropdownClick = (dropdown) => {
    if (isMobile) {
      setActiveDropdown(activeDropdown === dropdown ? null : dropdown)
    }
  }

  const handleDropdownHover = (dropdown, isEnter) => {
    if (!isMobile) {
      setActiveDropdown(isEnter ? dropdown : null)
    }
  }

  const handleServiceGroupClick = (groupIndex) => {
    if (isMobile) {
      setActiveServiceGroup(activeServiceGroup === groupIndex ? null : groupIndex)
    }
  }

  const handleServiceGroupHover = (groupIndex, isEnter) => {
    if (!isMobile) {
      // Desktop hover behavior is handled by CSS
    }
  }

  return (
    <React.Fragment>
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap" rel="stylesheet" />
      </Helmet>
      <nav className="navbar">
        <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/icon.svg" alt="Bisma Logo" className="navbar-logo-icon" />
        </Link>
        
        <button 
          className="menu-icon" 
          onClick={toggleMenu}
          aria-label="Menu de navigation"
          aria-expanded={isOpen}
        >
          <div className={isOpen ? "hamburger active" : "hamburger"}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <ul className={isOpen ? "nav-menu active" : "nav-menu"}>
          <li className="nav-item">
            <Link to="/" className="nav-link" activeClassName="active">
              Accueil
            </Link>
          </li>
          <li 
            className={`nav-item dropdown ${activeDropdown === 'services' ? 'active' : ''}`}
            onMouseEnter={() => handleDropdownHover('services', true)}
            onMouseLeave={() => handleDropdownHover('services', false)}
          >
            <div 
              className="nav-link dropdown-toggle"
              onClick={() => handleDropdownClick('services')}
            >
              Nos Services
              <span className="dropdown-arrow">▼</span>
            </div>
            <div 
              className="dropdown-menu services-dropdown"
              onMouseEnter={() => handleDropdownHover('services', true)}
              onMouseLeave={() => handleDropdownHover('services', false)}
            >
              <div className="services-dropdown-container">
                {allServiceGroups.map((group, index) => {
                  // If category has only one item, make it directly clickable
                  if (group.nodes.length === 1) {
                    const service = group.nodes[0];
                    return (
                      <div key={index} className="services-group">
                        <Link 
                          to={`/services/${service.slug}`} 
                          className="services-category single-item"
                          style={{color: "white"}}
                        >
                          <span className="category-title" style={{color: "white"}}>{group.fieldValue}</span>
                        </Link>
                      </div>
                    );
                  }
                  
                  // For categories with multiple items, show dropdown
                  return (
                    <div 
                      key={index} 
                      className={`services-group ${activeServiceGroup === index ? 'active' : ''}`}
                    >
                      <div 
                        className="services-category" 
                        style={{color: "white"}}
                        onClick={() => handleServiceGroupClick(index)}
                      >
                        <span className="category-title" style={{color: "white"}}>{group.fieldValue}</span>
                        <span className="dropdown-arrow">›</span>
                      </div>
                      <div className="services-submenu">
                        {group.nodes.map(service => (
                          <Link 
                            key={service.id}
                            to={`/services/${service.slug}`} 
                            className="dropdown-item"
                            activeClassName="active"
                            partiallyActive={true}
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div className="services-dropdown-footer">
                  <Link to="/services" className="see-all-services">
                    Voir tous les services
                  </Link>
                </div>
              </div>
            </div>
          </li>
          <li className="nav-item">
            <Link to="/services/renovation-dampleur" className="nav-link" activeClassName="active">
              Rénovation d'ampleur
            </Link>
          </li>
          
          
          <li 
            className={`nav-item dropdown ${activeDropdown === 'about' ? 'active' : ''}`}
            onMouseEnter={() => handleDropdownHover('about', true)}
            onMouseLeave={() => handleDropdownHover('about', false)}
          >
            <div 
              className="nav-link dropdown-toggle"
              onClick={() => handleDropdownClick('about')}
            >
              Qui sommes nous
              <span className="dropdown-arrow">▼</span>
            </div>
            <ul className="dropdown-menu">
              <li><Link to="/a-propos/notre-equipe" className="dropdown-item" activeClassName="active" partiallyActive={true}>Notre équipe</Link></li>
              <li><Link to="/a-propos/nos-valeurs" className="dropdown-item" activeClassName="active" partiallyActive={true}>Nos valeurs</Link></li>
              <li><Link to="/a-propos/nos-realisations" className="dropdown-item" activeClassName="active" partiallyActive={true}>Nos réalisations</Link></li>
              <li><Link to="/testimonials" className="dropdown-item" activeClassName="active">Témoignages</Link></li>
               <li><Link to="/blog" className="dropdown-item" activeClassName="active">Blog</Link></li>
            </ul>
          </li>
          
          <li className="nav-item">
            <Link to="/a-propos/parrainage" className="nav-link" activeClassName="active">
              Parrainage
            </Link>
          </li>
          
          <li className="nav-item">
            <Link to="/contact" className="nav-link" activeClassName="active">
              Contact
            </Link>
          </li>
          
          <li className="nav-item phone-number">
            <a href="tel:+33 9 83 51 94 98" className="phone-link">
              <span className="phone-icon">📞</span>
              <span>+33 9 83 51 94 98</span>
            </a>
          </li>
        </ul>
      </div>
    </nav>
    </React.Fragment>
  )
}

export default Navbar
